import { Briefcase, Calendar, CheckCircle, DollarSign, Edit, Eye, MapPin, Pause, Play, TrendingUp, Users } from 'lucide-react';
import jobPostingApi from '../../../api/jobPosting';
import Button from '../../../components/common/Button';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

const JobPostingCard = ({ job, onNavigate, onUpdate, onSelect, isSelected }) => {
  const [jobPostStats, setJobPostStats] = useState({})
  const fetchJobStats = async () => {
    try {
      const jobStats = await jobPostingApi.getJobViewStats(job.id)
      if (jobStats.status === 200)
        setJobPostStats(jobStats.data);
    } catch (error) {
      console.error("Something went wrong while fetching total views", error)
    }

  }

  useEffect(() => {
    fetchJobStats()
  }, [])
  const handleStatusToggle = async (e) => {
    e.stopPropagation();
    const newStatus = job.status === 'active' ? 'draft' : 'active';
    try {
      const response = await jobPostingApi.updateJobPosting(job.id, { status: newStatus });
      toast.success(`Job ${newStatus === 'active' ? 'resumed' : 'paused'} successfully`);
      onUpdate(response.data);
    } catch (error) {
      toast.error("Failed to toggle job status");
      console.error("Failed to toggle job status", error);
    }
  };

  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    onSelect(job.id);
  };

  const jobViews = jobPostStats.total_views

  const conversionRate = (jobViews > 0 ? ((job.applicants_count || 0) / jobViews) * 100 : 0).toFixed(2);

  return (
    <div
      onClick={() => onNavigate(`/post-job?edit=${job.id}`)}
      className="group bg-white border border-gray-200 rounded-2xl p-8 shadow-lg hover:shadow-xl hover:border-primary-300 transition-all duration-300 cursor-pointer relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-primary-50 opacity-0 group-hover:opacity-30 transition-opacity duration-300"></div>

      <div className="relative z-10">
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center gap-6">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={handleCheckboxClick}
              onClick={handleCheckboxClick}
              className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 transition-all duration-300 scale-110"
            />
            <div>
              <div className="flex items-center gap-3 mb-3">
                <h3 className="text-2xl font-bold text-gray-900 group-hover:text-primary-700 transition-colors duration-300">
                  {job.title}
                </h3>
                {job.is_urgent && (
                  <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-bold animate-pulse">
                    URGENT
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-4 py-2 rounded-xl text-sm font-bold ${job.status === 'active'
                    ? 'bg-green-100 text-green-700'
                    : job.status === 'draft'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                  {job.status.toUpperCase()}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center text-sm text-gray-500 gap-x-6 gap-y-2 mb-6 pb-6 border-b border-gray-100">
          {/* <div className="flex items-center gap-2">
                <Briefcase size={16} className="text-primary-500" />
                <span className="font-medium">{job.department || 'N/A'}</span>
              </div> */}
          <div className="flex items-center gap-2">
            <MapPin size={16} className="text-primary-500" />
            <span className="font-medium">{job.location}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-primary-500" />
            <span className="font-medium">{job.job_type}</span>
          </div>
          <div className="flex items-center gap-2">
            <DollarSign size={16} className="text-primary-500" />
            <span className="font-medium">{job.salary_min && job.salary_max ? `${job.salary_min}-${job.salary_max}` : 'Not specified'}</span>
          </div>
        </div>

        <div className="flex items-center text-sm text-gray-500 mb-6">
          <span>Posted: {new Date(job.created_at).toLocaleDateString()}</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-blue-50 border border-blue-200 p-6 rounded-2xl text-center hover:bg-blue-100 transition-colors duration-300">
            <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Users className="w-6 h-6 text-white" />
            </div>
            <p className="text-4xl font-bold text-blue-800 mb-2">{job.applicants_count || 0}</p>
            <p className="text-blue-700 font-medium">Applicants</p>
          </div>
          <div className="bg-green-50 border border-green-200 p-6 rounded-2xl text-center hover:bg-green-100 transition-colors duration-300">
            <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Eye className="w-6 h-6 text-white" />
            </div>
            <p className="text-4xl font-bold text-green-800 mb-2">{jobViews || 0}</p>
            <p className="text-green-700 font-medium">Views</p>
          </div>
          <div className="bg-purple-50 border border-purple-200 p-6 rounded-2xl text-center hover:bg-purple-100 transition-colors duration-300">
            <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center mx-auto mb-4">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <p className="text-4xl font-bold text-purple-800 mb-2">{conversionRate}%</p>
            <p className="text-purple-700 font-medium">Conversion</p>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div className="flex items-center text-sm">
            {job.status === 'active' ? (
              <>
                <CheckCircle size={20} className="text-green-600 mr-3" />
                <span className="text-green-700 font-semibold">Actively recruiting</span>
              </>
            ) : (
              <>
                <Pause size={20} className="text-yellow-600 mr-3" />
                <span className="text-yellow-700 font-semibold">Recruitment paused</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="primary"
              size="sm"
              onClick={(e) => { e.stopPropagation(); onNavigate(`/view-applications/${job.id}`) }}
            >
              View Applications ({job.applicants_count || 0})
            </Button>
            {/* <Button 
                  variant="secondary"
                  size="sm" 
                  onClick={handleStatusToggle}
                >
                  {job.status === 'active' ? (
                    <>
                      <Pause size={16} className="mr-2" />
                      Pause
                    </>
                  ) : (
                    <>
                      <Play size={16} className="mr-2" />
                      Resume
                    </>
                  )}
                </Button> */}
            <Button
              variant="outline"
              size="sm"
              className="p-3 h-10 w-10"
              onClick={(e) => { e.stopPropagation(); onNavigate(`/post-job?edit=${job.id}`) }}
            >
              <Edit size={16} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobPostingCard;